import React, { useState } from "react";
import {
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  TextField,
  DialogContentText,
  Typography,
} from "@mui/material";
import { Formik, Form, Field } from "formik";
import * as Yup from "yup";
import SelectRol from "../../components/SelectRol-Usuario/SelectRol";
import CloseIcon from '@mui/icons-material/Close';
import DoneOutlineIcon from '@mui/icons-material/DoneOutline';
import { getProyectosApiBaseUrl } from "../../utils/proyectosApiBaseUrl";

interface CrearUsuarioProps {
  open: boolean;
  onClose: () => void;
  onSubmit: (values: any) => void;
}

const validationSchema = Yup.object({
  nombre: Yup.string().required("Nombre es requerido"),
  usuario: Yup.string().required("Usuario es requerido"),
  correo: Yup.string().email("Correo invalido").required("Correo es requerido"),
  contraseña: Yup.string().min(6, "Minimo 6 caracteres").optional(),
  rol: Yup.string().required("Rol es requerido"),
});

const CrearUsuario: React.FC<CrearUsuarioProps> = ({
  open,
  onClose,
  onSubmit,
}) => {
  const [successDialogOpen, setSuccessDialogOpen] = useState(false);
  const [successMessage, setSuccessMessage] = useState<string | null>(null);

  const handleSubmit = async (values: any) => {
    try {
      const storedToken = localStorage.getItem("authToken");
      const token = storedToken ? atob(storedToken) : "";
      const storedUser = localStorage.getItem("user");
      const user = storedUser ? atob(storedUser) : "";

      const updatedValues = {
        ...values,
        correo: String(values.correo || "").trim().toLowerCase(),
        usuarioCreador: user,
      };

      const response = await fetch(
        `${getProyectosApiBaseUrl()}/usuarios`,
        {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
            authorization: `Bearer ${token}`,
          },
          body: JSON.stringify(updatedValues),
        }
      );

      if (!response.ok) {
        const err = await response.json().catch(() => ({}));
        throw new Error(err?.message || "No fue posible crear el usuario.");
      }

      const data = await response.json();
      setSuccessMessage(
        data?.contraseña_generada
          ? "Usuario creado. Se envió un correo con usuario y contraseña generada."
          : "Usuario creado. Se envió un correo con las credenciales de acceso.",
      );
      setSuccessDialogOpen(true);
      onSubmit(data);
      onClose();
    } catch (error: any) {
      console.error("Error:", error);
      alert(error?.message || "Error al crear usuario.");
    }
  };

  const handleCloseSuccessDialog = () => {
    setSuccessDialogOpen(false);
    setTimeout(() => {
      window.location.reload();
    }, 500);
  };

  return (
    <>
      <Dialog open={open} onClose={onClose} fullWidth maxWidth="md">
        <DialogTitle>Crear Usuario</DialogTitle>
        <Formik
          initialValues={{
            nombre: "",
            usuario: "",
            correo: "",
            contraseña: "",
            rol: "",
          }}
          validationSchema={validationSchema}
          onSubmit={(values, { setSubmitting }) => {
            setSubmitting(false);
            handleSubmit(values);
          }}
        >
          {({ isSubmitting, errors, touched }) => (
            <Form>
              <DialogContent>
                <Field
                  as={TextField}
                  name="nombre"
                  label="Nombre"
                  fullWidth
                  error={touched.nombre && !!errors.nombre}
                  helperText={touched.nombre && errors.nombre}
                  margin="normal"
                />
                <Field
                  as={TextField}
                  name="usuario"
                  label="Usuario (inicio de sesion)"
                  fullWidth
                  error={touched.usuario && !!errors.usuario}
                  helperText={touched.usuario && errors.usuario}
                  margin="normal"
                />
                <Field
                  as={TextField}
                  name="correo"
                  label="Correo electronico"
                  type="email"
                  fullWidth
                  error={touched.correo && !!errors.correo}
                  helperText={touched.correo && errors.correo}
                  margin="normal"
                />
                <Field
                  as={TextField}
                  name="contraseña"
                  label="Contraseña (opcional)"
                  type="password"
                  fullWidth
                  error={touched.contraseña && !!errors.contraseña}
                  helperText={
                    (touched.contraseña && errors.contraseña) ||
                    "Si no la indica, se generará automáticamente y se enviará por correo."
                  }
                  margin="normal"
                />
                <SelectRol name="rol" onlyProjectRoles />
                <Typography variant="caption" color="text.secondary" display="block" sx={{ mt: 1 }}>
                  Al crear el usuario se aplican los permisos base del rol elegido. Para permisos adicionales use el menu
                  «Permisos gestion proyectos» (solo administrador).
                </Typography>
              </DialogContent>
              <DialogActions>
                <Button onClick={onClose} color="error" variant="contained" endIcon={<CloseIcon />}>
                  Cancelar
                </Button>
                <Button
                  type="submit"
                  color="primary"
                  variant="contained"
                  endIcon={<DoneOutlineIcon />}
                  disabled={isSubmitting || Object.keys(errors).length > 0}
                >
                  Enviar
                </Button>
              </DialogActions>
            </Form>
          )}
        </Formik>
      </Dialog>

      <Dialog open={successDialogOpen} onClose={handleCloseSuccessDialog}>
        <DialogTitle>Éxito</DialogTitle>
        <DialogContent>
          <DialogContentText>{successMessage}</DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button
            onClick={handleCloseSuccessDialog}
            color="primary"
            variant="contained"
          >
            Aceptar
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
};

export default CrearUsuario;
